import { Card, CardContent, CardHeader, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DndProvider, SortableList, SortableItem, getItemClass, getListClass } from '__core/modules/components/dnd';
import React, { useState, useCallback, useMemo } from 'react';

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ListCard = styled(Card)({
  flex: 1,
  minWidth: 250,
});

const DroppableArea = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDraggingOver',
})(({ theme, isDraggingOver }) => ({
  minHeight: 120,
  padding: theme.spacing(1),
  transition: 'background-color 0.2s ease',
  backgroundColor: isDraggingOver ? theme.palette.action.hover : theme.palette.background.default,
}));

const DraggablePaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})(({ theme, isDragging }) => ({
  marginBottom: theme.spacing(1),
  transition: 'transform 0.2s ease, background-color 0.2s ease',
  ...(isDragging && {
    transform: 'rotate(2deg)',
    backgroundColor: theme.palette.warning.light,
  }),
}));

const JsonPreview = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  '& pre': {
    margin: 0,
    fontSize: '0.875rem',
  },
}));

// Componente auxiliar para el área donde se pueden soltar elementos
const DroppableList = ({ listId, items }) => {
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  return (
    <SortableList droppableId={listId} items={itemIds} disabled={listId === 'materials'}>
      {({ isOver }) => (
        <DroppableArea isDraggingOver={isOver}>
          {items.map((item) => (
            <DraggableItem key={item.id} item={item} listId={listId} />
          ))}
        </DroppableArea>
      )}
    </SortableList>
  );
};

// Componente auxiliar para los elementos arrastrables
const DraggableItem = ({ item, listId }) => (
  <SortableItem id={item.id} disabled={listId !== 'materials'}>
    {({ isDragging, attributes, listeners, setNodeRef, style }) => (
      <DraggablePaper
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        elevation={isDragging ? 3 : 1}
        isDragging={isDragging}
      >
        <CardContent>{item.name}</CardContent>
      </DraggablePaper>
    )}
  </SortableItem>
);

const DragDropLists = () => {
  const [lists, setLists] = useState({
    materials: {
      title: 'Materiales',
      items: [
        { id: '1', name: 'Cat' },
        { id: '2', name: 'Dog' },
        { id: '3', name: 'Bird' },
        { id: '4', name: 'Moto' },
        { id: '5', name: 'Coche' },
        { id: '6', name: 'Pepe' },
      ],
    },
    session_1: {
      title: 'Sesión 1',
      items: [],
    },
    session_4: {
      title: 'Sesión 4',
      items: [],
    },
  });

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;

      // Si no hay destino válido, salir
      if (!destination) return;

      // Si el origen y destino son el mismo y el índice es el mismo, salir
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      const sourceList = lists[source.droppableId];
      const destList = lists[destination.droppableId];

      // Crear copias de los arrays para manipular
      const sourceItems = Array.from(sourceList.items);
      const destItems = Array.from(destList.items);

      // Si el origen es "materials", crear una copia del elemento
      if (source.droppableId === 'materials') {
        const itemToCopy = sourceList.items[source.index];
        const newItem = {
          ...itemToCopy,
          id: `${itemToCopy.id}-${Date.now()}`, // Crear un ID único para la copia
        };
        destItems.splice(destination.index, 0, newItem);

        setLists({
          ...lists,
          [destination.droppableId]: {
            ...destList,
            items: destItems,
          },
        });
      } else {
        // Para otras listas, mover el elemento
        const [removedItem] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, removedItem);

        setLists({
          ...lists,
          [source.droppableId]: {
            ...sourceList,
            items: sourceItems,
          },
          [destination.droppableId]: {
            ...destList,
            items: destItems,
          },
        });
      }
    },
    [lists]
  );

  // Adaptar evento @dnd-kit al formato del hook
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (!over) {
        return;
      }

      const sourceContainerId = active.data?.current?.sortable?.containerId;
      const destContainerId = over.data?.current?.sortable?.containerId || over.id;
      const sourceIndex = active.data?.current?.sortable?.index ?? 0;
      const destIndex = over.data?.current?.sortable?.index ?? 0;

      onDragEnd({
        source: { droppableId: sourceContainerId, index: sourceIndex },
        destination: { droppableId: destContainerId, index: destIndex },
        draggableId: String(active.id),
      });
    },
    [onDragEnd]
  );

  return (
    <DndProvider onDragEnd={handleDragEnd}>
      <Root>
        <Container>
          {Object.entries(lists).map(([listId, list]) => (
            <ListCard key={listId}>
              <CardHeader title={list.title} titleTypographyProps={{ variant: 'h6' }} />
              <CardContent>
                <DroppableList listId={listId} items={list.items} />
              </CardContent>
            </ListCard>
          ))}
        </Container>

        <JsonPreview>
          <pre>{JSON.stringify(lists, null, 2)}</pre>
        </JsonPreview>
      </Root>
    </DndProvider>
  );
};

export default DragDropLists;
